<template lang="pug">
.team
  //- ============ HERO ============
  header.team__hero
    .team__hero-grid
    .team__hero-glow.team__hero-glow--blue
    .team__hero-glow.team__hero-glow--red
    .team__hero-inner
      p.team__eyebrow
        span.team__eyebrow-pip
        | Minha equipe
      .team__head
        .team__logo(v-if="hasTeam")
          img.team__logo-img(v-if="teamLogo" :src="teamLogo" alt="")
          FontAwesomeIcon(v-else :icon="['fas', 'users']")
        h1.team__title {{ hasTeam && team ? team.name : 'Você ainda não tem equipe' }}
      .team__tags(v-if="hasTeam")
        span.team__tag(v-if="isCaptain")
          FontAwesomeIcon.team__tag-ic(:icon="['fas', 'star']")
          | Capitão
        span.team__tag.team__tag--muted(v-else)
          FontAwesomeIcon.team__tag-ic(:icon="['fas', 'user']")
          | Membro
        span.team__tag.team__tag--muted(v-if="team")
          FontAwesomeIcon.team__tag-ic(:icon="['fas', 'users']")
          | {{ members.length }} membro(s)
        span.team__tag.team__tag--muted(v-if="team")
          FontAwesomeIcon.team__tag-ic(:icon="['fas', 'robot']")
          | {{ robots.length }} robô(s)

  .team__wrap
    //- ---------- ESTADO: SEM EQUIPE ----------
    .team__state(v-if="!hasTeam")
      span.team__state-ic
        FontAwesomeIcon(:icon="['fas', 'users']")
      .team__state-txt
        strong Crie sua equipe
        span Ao criar uma equipe você vira o capitão e pode adicionar membros, robôs e inscrevê-los nas competições.
      button.team__state-btn(@click="doShowModal('create')")
        span Criar equipe
        FontAwesomeIcon.team__state-btn-ic(:icon="['fas', 'arrow-right']")

    //- ---------- ESTADO: COM EQUIPE ----------
    template(v-else)
      //- MEMBROS
      section.team__section
        .team__section-head
          h2.team__section-title Membros
          button.team__add(v-if="isCaptain" @click="doShowModal('member')")
            FontAwesomeIcon(:icon="['fas', 'user-plus']")
            | Adicionar membro
        .team__list
          .team__row(v-for="m in members" :key="m.id")
            span.team__row-avatar {{ initials(m.name) }}
            .team__row-body
              span.team__row-name {{ m.name || 'Sem nome' }}
              span.team__row-sub {{ m.email }}
            span.team__row-badge(v-if="m.is_captain")
              FontAwesomeIcon.team__row-badge-ic(:icon="['fas', 'star']")
              | Capitão
            button.team__row-del(
              v-if="isCaptain && !m.is_captain"
              @click="doRemoveMember(m.id)"
              title="Remover da equipe"
            )
              FontAwesomeIcon(:icon="['fas', 'times']")

      //- ROBÔS
      section.team__section
        .team__section-head
          h2.team__section-title Robôs
          button.team__add(v-if="isCaptain" @click="doShowModal('robot')")
            FontAwesomeIcon(:icon="['fas', 'plus']")
            | Adicionar robô
        .team__list(v-if="robots.length")
          .team__row(v-for="r in robots" :key="r.id" :class="{ 'team__row--off': !r.active }")
            span.team__row-avatar.team__row-avatar--bot
              img.team__row-photo(v-if="robotImages[r.id]" :src="robotImages[r.id]" alt="")
              FontAwesomeIcon(v-else :icon="['fas', 'robot']")
            .team__row-body
              span.team__row-name {{ r.name }}
              span.team__row-sub {{ categoryName(r.category_id) }}
              span.team__row-desc(v-if="r.description") {{ r.description }}
            span.team__row-badge.team__row-badge--off(v-if="!r.active")
              FontAwesomeIcon.team__row-badge-ic(:icon="['fas', 'ban']")
              | Inativo
            span.team__row-badge.team__row-badge--on(v-else)
              FontAwesomeIcon.team__row-badge-ic(:icon="['fas', 'circle-check']")
              | Ativo
            button.team__row-del.team__row-edit(
              v-if="isCaptain"
              @click="doEditRobot(r)"
              title="Editar robô"
            )
              FontAwesomeIcon(:icon="['fas', 'edit']")
        .team__empty(v-else) Nenhum robô cadastrado ainda.

      //- AÇÕES DO CAPITÃO
      section.team__section(v-if="isCaptain")
        h2.team__section-title Ações
        .team__actions
          button.actcard(@click="doShowModal('rename')")
            span.actcard__ic
              FontAwesomeIcon(:icon="['fas', 'edit']")
            span.actcard__txt
              strong Editar equipe
              span Alterar o nome e a logo da equipe
            FontAwesomeIcon.actcard__arrow(:icon="['fas', 'arrow-right']")
          button.actcard(@click="doShowModal('register')")
            span.actcard__ic
              FontAwesomeIcon(:icon="['fas', 'trophy']")
            span.actcard__txt
              strong Inscrever no evento
              span Inscrever robôs e membros numa competição
            FontAwesomeIcon.actcard__arrow(:icon="['fas', 'arrow-right']")

      //- MEMBRO (não-capitão): sair da equipe
      section.team__section(v-else)
        h2.team__section-title Sua participação
        .team__hint
          span.team__hint-ic
            FontAwesomeIcon(:icon="['fas', 'user']")
          .team__hint-txt
            strong Você é membro desta equipe
            span Apenas o capitão pode gerenciar a equipe e inscrever nas competições.
          button.team__hint-btn(@click="doLeaveTeam")
            FontAwesomeIcon.team__hint-btn-ic(:icon="['fas', 'sign-out-alt']")
            span Sair da equipe

  //- ============ MODAL ============
  Modal(
    :visible="showModal"
    @close="doCloseModal"
    @emit="doCloseModal"
  )
    .tmodal
      //- CRIAR EQUIPE
      .tmodal__step(v-if="step === 'create'")
        h3.tmodal__title Criar equipe
        label.tmodal__label Nome da equipe
        input.tmodal__input(
          type="text"
          placeholder="Digite o nome da equipe"
          v-model="teamName"
        )
        p.tmodal__err(v-if="feedback") {{ feedback }}
        .tmodal__foot
          Button(text="Criar" border="base" @click="doCreateTeam")

      //- EDITAR EQUIPE (nome + logo)
      .tmodal__step(v-if="step === 'rename'")
        h3.tmodal__title Editar equipe
        .tmodal__photo
          .tmodal__photo-preview
            img(v-if="teamLogo" :src="teamLogo" alt="")
            FontAwesomeIcon(v-else :icon="['fas', 'users']")
          label.tmodal__photo-btn
            FontAwesomeIcon(:icon="['fas', 'image']")
            | {{ teamLogo ? 'Trocar logo' : 'Adicionar logo' }}
            input.tmodal__photo-input(type="file" accept="image/*" @change="doSelectTeamLogo")
        label.tmodal__label Nome da equipe
        input.tmodal__input(
          type="text"
          placeholder="Digite o novo nome"
          v-model="teamName"
        )
        p.tmodal__err(v-if="feedback") {{ feedback }}
        .tmodal__foot
          Button(text="Salvar" border="base" @click="doRenameTeam")

      //- ADICIONAR MEMBRO
      .tmodal__step(v-if="step === 'member'")
        h3.tmodal__title Adicionar membro
        label.tmodal__label E-mail do usuário
        input.tmodal__input(
          type="email"
          placeholder="email@exemplo.com"
          v-model="memberEmail"
        )
        p.tmodal__hint O usuário já precisa ter conta no sistema.
        p.tmodal__err(v-if="feedback") {{ feedback }}
        .tmodal__foot
          Button(text="Adicionar" border="base" @click="doAddMember")

      //- ADICIONAR / EDITAR ROBÔ
      .tmodal__step(v-if="step === 'robot'")
        h3.tmodal__title {{ editingRobotId ? 'Editar robô' : 'Adicionar robô' }}
        .tmodal__photo
          .tmodal__photo-preview
            img(v-if="robotPhoto" :src="robotPhoto" alt="")
            FontAwesomeIcon(v-else :icon="['fas', 'robot']")
          label.tmodal__photo-btn
            FontAwesomeIcon(:icon="['fas', 'image']")
            | {{ robotPhoto ? 'Trocar foto' : 'Adicionar foto' }}
            input.tmodal__photo-input(type="file" accept="image/*" @change="doSelectRobotPhoto")
        label.tmodal__label Nome do robô
        input.tmodal__input(
          type="text"
          placeholder="Digite o nome do robô"
          v-model="robotName"
        )
        label.tmodal__label Categoria
        Select.tmodal__select(
          defaultText="Selecionar categoria"
          :options="takeCategories"
          :value="selectedCategory.value"
          type="drop"
          @emit="doSetSelectedCategory"
        )
        label.tmodal__label Descrição
        textarea.tmodal__textarea(
          rows="3"
          placeholder="Arma, peso, características..."
          v-model="robotDescription"
        )
        label.tmodal__switch
          input(type="checkbox" v-model="robotActive")
          span.tmodal__switch-track
          span.tmodal__switch-lbl Robô ativo
        p.tmodal__err(v-if="feedback") {{ feedback }}
        .tmodal__foot
          Button(:text="editingRobotId ? 'Salvar' : 'Adicionar'" border="base" @click="doSaveRobot")

      //- INSCREVER NO EVENTO
      .tmodal__step(v-if="step === 'register'")
        h3.tmodal__title Inscrever no evento
        label.tmodal__label Evento
        Select.tmodal__select(
          defaultText="Selecionar evento"
          :options="eventOptions"
          type="drop"
          @emit="doSetSelectedEvent"
        )
        template(v-if="robots.length")
          label.tmodal__label Robôs
          .tmodal__picks
            label.pick(
              v-for="r in robots"
              :key="'r' + r.id"
              :class="{ 'pick--on': selectedRobots.includes(r.id) }"
            )
              input.pick__input(type="checkbox" :value="r.id" v-model="selectedRobots")
              span.pick__check
                FontAwesomeIcon(:icon="['fas', 'check']")
              span.pick__name {{ r.name }}
        template(v-if="members.length")
          label.tmodal__label Membros
          .tmodal__picks
            label.pick(
              v-for="m in members"
              :key="'m' + m.id"
              :class="{ 'pick--on': selectedMembers.includes(m.id) }"
            )
              input.pick__input(type="checkbox" :value="m.id" v-model="selectedMembers")
              span.pick__check
                FontAwesomeIcon(:icon="['fas', 'check']")
              span.pick__name {{ m.name || m.email }}
        p.tmodal__err(v-if="feedback") {{ feedback }}
        p.tmodal__ok(v-if="registerOk") Inscrição realizada!
        .tmodal__foot
          Button(text="Inscrever" border="base" @click="doRegister")
</template>

<script>
import {
  mapGetters,
  mapActions
} from 'vuex'

import {
  Button,
  Select
} from '@/components/atoms'

import {
  Modal
} from '@/components/molecules'

import { downscaleImage } from '@/utils/image'

export default {
  name: 'MinhaEquipe',
  components: {
    Button,
    Select,
    Modal
  },
  data: () => ({
    showModal: false,
    step: 'create',
    feedback: '',
    registerOk: false,
    teamName: '',
    memberEmail: '',
    robotName: '',
    robotDescription: '',
    robotActive: true,
    robotPhoto: '',
    robotPhotoChanged: false,
    editingRobotId: null,
    robotImages: {},
    teamLogo: '',
    teamLogoChanged: false,
    selectedCategory: {},
    selectedEvent: {},
    selectedRobots: [],
    selectedMembers: []
  }),
  created () {
    this.setCategories()
    this.setEvents()
    // Recarrega o usuário para ter o team_id fresco e então carrega a equipe.
    this.setUser({ user_id: this.getUser.id }).then(() => {
      if (!this.getUser || !this.getUser.id) {
        this.$router.push('SignIn')
        return
      }
      this.loadTeam()
    })
  },
  computed: {
    ...mapGetters('users', ['getUser']),
    ...mapGetters('teams', ['getCurrentTeam']),
    ...mapGetters('categories', ['getCategories']),
    ...mapGetters('events', ['getVisibleEvents']),
    hasTeam () {
      return !!(this.getUser && this.getUser.team_id)
    },
    team () {
      return this.getCurrentTeam
    },
    ownerId () {
      return this.team ? this.team.owner_id : null
    },
    isCaptain () {
      return !!(this.getUser && this.ownerId != null && this.getUser.id === this.ownerId)
    },
    members () {
      return (this.team && this.team.members) || []
    },
    robots () {
      return (this.team && this.team.robots) || []
    },
    takeCategories () {
      return [...this.getCategories].map(c => ({ text: c.name, value: c.id }))
    },
    eventOptions () {
      return [...this.getVisibleEvents].map(e => ({ text: e.name, value: e.id }))
    }
  },
  methods: {
    ...mapActions('users', ['setUser', 'setRegisterEventParticipants']),
    ...mapActions('teams', ['setCreateTeam', 'setTeamDetail', 'setUpdateTeam', 'setAddTeamMember', 'setRemoveTeamMember', 'setTeamImage', 'fetchTeamImage']),
    ...mapActions('robots', ['setCreateRobot', 'setUpdateRobot', 'setRobotImage', 'fetchRobotImage', 'setRegisterEventRobots']),
    ...mapActions('categories', ['setCategories']),
    ...mapActions('events', ['setEvents']),
    loadTeam () {
      if (this.hasTeam) {
        this.setTeamDetail({ team_id: this.getUser.team_id }).then(() => this.loadRobotImages())
        this.fetchTeamImage(this.getUser.team_id).then(img => { this.teamLogo = img || '' })
      }
    },
    loadRobotImages () {
      // Fotos ficam fora do get_team (payload pesado); carrega sob demanda por robô.
      this.robots.forEach(r => {
        this.fetchRobotImage(r.id).then(img => {
          if (img) this.$set(this.robotImages, r.id, img)
        })
      })
    },
    initials (name) {
      const parts = (name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    categoryName (id) {
      const c = this.getCategories.find(cat => cat.id === id)
      return c ? c.name : 'Sem categoria'
    },
    doShowModal (step) {
      this.step = step
      this.feedback = ''
      this.registerOk = false
      if (step === 'rename') {
        this.teamName = this.team ? this.team.name : ''
        this.teamLogoChanged = false
      }
      if (step === 'register') {
        this.selectedRobots = []
        this.selectedMembers = []
        this.selectedEvent = {}
      }
      if (step === 'robot') this.resetRobotForm()
      this.showModal = true
    },
    resetRobotForm () {
      this.editingRobotId = null
      this.robotName = ''
      this.robotDescription = ''
      this.robotActive = true
      this.robotPhoto = ''
      this.robotPhotoChanged = false
      this.selectedCategory = {}
    },
    doCloseModal () {
      this.showModal = false
    },
    doSetSelectedCategory (value) {
      this.selectedCategory = { ...value }
    },
    doSetSelectedEvent (value) {
      this.selectedEvent = { ...value }
    },
    doCreateTeam () {
      this.feedback = ''
      const name = this.teamName.trim()
      if (!name) {
        this.feedback = 'Informe o nome da equipe.'
        return
      }
      this.setCreateTeam({ name }).then((response) => {
        // sucesso = array [{id,...}]; falha (nome duplicado) = {success:false,error};
        // erro de rede/servidor = null. Só avança quando a equipe foi realmente criada.
        const created = Array.isArray(response) && response[0] && response[0].id
        if (!created) {
          this.feedback = (response && response.error) || 'Não foi possível criar a equipe. O nome pode já estar em uso.'
          return null
        }
        this.showModal = false
        this.teamName = ''
        // recarrega o usuário (team_id agora setado) e a equipe → vira capitão
        return this.setUser({ user_id: this.getUser.id })
      }).then((r) => { if (r !== null) this.loadTeam() })
    },
    doSelectTeamLogo (e) {
      const files = e.target.files
      if (!files || !files.length) return
      const reader = new FileReader()
      reader.onload = () => {
        downscaleImage(reader.result).then(small => {
          this.teamLogo = small
          this.teamLogoChanged = true
        })
      }
      reader.readAsDataURL(files[0])
    },
    doRenameTeam () {
      const name = this.teamName.trim()
      if (!name) {
        this.feedback = 'Informe o novo nome.'
        return
      }
      const teamId = this.getUser.team_id
      this.setUpdateTeam({ team_id: teamId, name }).then(() => {
        if (this.teamLogoChanged && this.teamLogo) {
          return this.setTeamImage({ team_id: teamId, image: this.teamLogo.split('base64,')[1] })
        }
        return true
      }).then(() => {
        this.teamLogoChanged = false
        this.showModal = false
      })
    },
    doAddMember () {
      const email = this.memberEmail.trim()
      if (!email) {
        this.feedback = 'Informe o e-mail.'
        return
      }
      this.setAddTeamMember({ team_id: this.getUser.team_id, email }).then(response => {
        if (response && response.success) {
          this.showModal = false
          this.memberEmail = ''
        } else {
          this.feedback = (response && response.error) || 'Não foi possível adicionar o membro.'
        }
      })
    },
    doRemoveMember (userId) {
      this.setRemoveTeamMember({ team_id: this.getUser.team_id, user_id: userId })
    },
    doLeaveTeam () {
      this.setRemoveTeamMember({ team_id: this.getUser.team_id, user_id: this.getUser.id }).then(() => {
        return this.setUser({ user_id: this.getUser.id })
      })
    },
    doEditRobot (r) {
      this.step = 'robot'
      this.feedback = ''
      this.editingRobotId = r.id
      this.robotName = r.name
      this.robotDescription = r.description || ''
      this.robotActive = r.active !== false
      this.selectedCategory = r.category_id != null
        ? { text: this.categoryName(r.category_id), value: r.category_id }
        : {}
      this.robotPhoto = this.robotImages[r.id] || ''
      this.robotPhotoChanged = false
      this.showModal = true
    },
    doSelectRobotPhoto (e) {
      const files = e.target.files
      if (!files || !files.length) return
      const reader = new FileReader()
      reader.onload = () => {
        downscaleImage(reader.result).then(small => {
          this.robotPhoto = small
          this.robotPhotoChanged = true
        })
      }
      reader.readAsDataURL(files[0])
    },
    uploadRobotPhoto (robotId) {
      // envia o base64 cru (sem prefixo data-URI); o backend redimensiona 400x400
      if (!this.robotPhotoChanged || !this.robotPhoto) return Promise.resolve()
      return this.setRobotImage({ robot_id: robotId, image: this.robotPhoto.split('base64,')[1] })
    },
    doSaveRobot () {
      this.feedback = ''
      const name = this.robotName.trim()
      if (!name || !this.selectedCategory.value) {
        this.feedback = 'Informe o nome e a categoria do robô.'
        return
      }
      const payload = {
        name,
        class: this.selectedCategory.value,
        description: this.robotDescription.trim() || null,
        active: this.robotActive
      }
      if (this.editingRobotId) {
        this.setUpdateRobot({ robot_id: this.editingRobotId, ...payload })
          .then(response => {
            if (!response || response.success === false) {
              this.feedback = (response && response.error) || 'Não foi possível salvar o robô.'
              return null
            }
            return this.uploadRobotPhoto(this.editingRobotId)
          })
          .then(saved => { if (saved !== null) { this.showModal = false; this.loadTeam() } })
      } else {
        this.setCreateRobot({ ...payload, team: this.getUser.team_id })
          .then(response => {
            const id = response && response[0] && response[0].id
            if (!id) {
              this.feedback = 'Não foi possível criar o robô.'
              return null
            }
            return this.uploadRobotPhoto(id)
          })
          .then(saved => { if (saved !== null) { this.showModal = false; this.loadTeam() } })
      }
    },
    doRegister () {
      this.feedback = ''
      this.registerOk = false
      const eventId = this.selectedEvent.value
      if (!eventId) {
        this.feedback = 'Selecione um evento.'
        return
      }
      if (!this.selectedRobots.length && !this.selectedMembers.length) {
        this.feedback = 'Selecione ao menos um robô ou membro.'
        return
      }
      const tasks = []
      if (this.selectedRobots.length) {
        tasks.push(this.setRegisterEventRobots({
          owner: this.getUser.id,
          event_id: eventId,
          robot_ids: this.selectedRobots
        }))
      }
      if (this.selectedMembers.length) {
        tasks.push(this.setRegisterEventParticipants({
          owner: this.getUser.id,
          event_id: eventId,
          user_ids: this.selectedMembers
        }))
      }
      Promise.all(tasks).then(results => {
        const ok = results.every(r => r && (r.success || r.length))
        if (ok) {
          this.registerOk = true
          this.selectedRobots = []
          this.selectedMembers = []
        } else {
          this.feedback = 'Não foi possível concluir a inscrição.'
        }
      })
    }
  }
}
</script>

<style lang="sass" scoped>
$blue: #287AED
$blue-dark: #0F4FA8
$red: #DD3434
$ink: #0B0E14
$muted: #5C6673
$line: rgba(11, 14, 20, 0.10)
$paper: #FFFFFF
$page: #F2F4F8

$display: 'Saira Condensed', 'Lato', sans-serif
$mono: 'Space Mono', 'Lato', monospace

.team
  color: $ink
  background: $page
  min-height: calc(100vh - 64px)

  *
    box-sizing: border-box

  // ---------- HERO ----------
  &__hero
    position: relative
    overflow: hidden
    isolation: isolate
    padding: 40px 0 52px
    background: radial-gradient(circle at 16% 8%, #15233b 0%, $ink 52%, #05070b 100%)
    color: #fff

  &__hero-grid
    position: absolute
    inset: 0
    background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
    background-size: 42px 42px
    mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
    -webkit-mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
    z-index: -2

  &__hero-glow
    position: absolute
    width: 360px
    height: 360px
    border-radius: 50%
    filter: blur(110px)
    opacity: 0.5
    z-index: -1
    &--blue
      background: $blue
      top: -160px
      left: -90px
    &--red
      background: $red
      bottom: -190px
      right: -80px

  &__hero-inner
    position: relative
    max-width: 960px
    margin: 0 auto
    padding: 0 24px
    > *
      animation: team-rise .6s cubic-bezier(.2, .7, .2, 1) both
    .team__title
      animation-delay: .08s
    .team__tags
      animation-delay: .16s

  &__eyebrow
    display: inline-flex
    align-items: center
    gap: 10px
    font-family: $mono
    text-transform: uppercase
    letter-spacing: 0.22em
    font-size: 12px
    font-weight: 700
    color: rgba(255, 255, 255, 0.7)
    margin: 0 0 18px

  &__eyebrow-pip
    width: 8px
    height: 8px
    border-radius: 50%
    background: linear-gradient(135deg, #5AA0FF, $red)
    box-shadow: 0 0 0 4px rgba(40, 122, 237, 0.18)

  &__head
    display: flex
    align-items: center
    gap: 18px
    flex-wrap: wrap

  &__logo
    display: grid
    place-items: center
    flex-shrink: 0
    width: 72px
    height: 72px
    border-radius: 16px
    overflow: hidden
    font-size: 30px
    color: #fff
    background: linear-gradient(150deg, $blue, $blue-dark 55%, $red)
    border: 2px solid rgba(255, 255, 255, 0.16)

  &__logo-img
    width: 100%
    height: 100%
    object-fit: cover

  &__title
    font-family: $display
    font-weight: 800
    text-transform: uppercase
    font-size: clamp(30px, 4.4vw, 48px)
    line-height: 0.98
    letter-spacing: -0.015em
    margin: 0

  &__tags
    display: flex
    flex-wrap: wrap
    gap: 8px
    margin: 20px 0 0

  &__tag
    display: inline-flex
    align-items: center
    gap: 8px
    font-family: $mono
    font-size: 11px
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 0.06em
    color: #fff
    background: rgba(40, 122, 237, 0.18)
    border: 1px solid rgba(40, 122, 237, 0.4)
    border-radius: 999px
    padding: 6px 13px
    &-ic
      font-size: 11px
      color: #FFC857
    &--muted
      background: rgba(255, 255, 255, 0.10)
      border-color: rgba(255, 255, 255, 0.14)
      .team__tag-ic
        color: #6FB0FF

  // ---------- CORPO ----------
  &__wrap
    max-width: 960px
    margin: 0 auto
    padding: 40px 24px 64px

  &__state
    display: flex
    align-items: center
    gap: 18px
    padding: 24px
    background: $paper
    border: 1px solid $line
    border-radius: 16px
    flex-wrap: wrap
    &-ic
      display: grid
      place-items: center
      flex-shrink: 0
      width: 52px
      height: 52px
      border-radius: 12px
      font-size: 22px
      color: $blue
      background: rgba(40, 122, 237, 0.10)
    &-txt
      flex: 1 1 220px
      display: flex
      flex-direction: column
      gap: 4px
      strong
        font-family: $display
        text-transform: uppercase
        font-size: 20px
        font-weight: 700
        color: $ink
      span
        font-size: 14px
        color: $muted
    &-btn
      display: inline-flex
      align-items: center
      gap: 10px
      cursor: pointer
      font-family: $display
      font-weight: 600
      text-transform: uppercase
      letter-spacing: 0.06em
      font-size: 14px
      color: #fff
      background: $blue
      border: none
      border-radius: 6px
      padding: 11px 20px
      box-shadow: 0 10px 24px rgba(40, 122, 237, 0.32)
      transition: transform .18s ease, box-shadow .18s ease
      &:hover
        transform: translateY(-2px)
        box-shadow: 0 14px 30px rgba(40, 122, 237, 0.42)
      &-ic
        font-size: 12px

  // ---------- SEÇÕES ----------
  &__section
    margin-bottom: 8px
    & + .team__section
      margin-top: 36px

  &__section-head
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    flex-wrap: wrap
    margin: 0 0 18px

  &__section-title
    display: flex
    align-items: center
    gap: 12px
    font-family: $display
    text-transform: uppercase
    letter-spacing: 0.01em
    font-size: 22px
    font-weight: 700
    margin: 0
    &::before
      content: ''
      width: 4px
      height: 20px
      border-radius: 2px
      background: linear-gradient(180deg, $blue, $red)

  &__add
    display: inline-flex
    align-items: center
    gap: 8px
    cursor: pointer
    font-family: $mono
    font-size: 12px
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 0.04em
    color: $blue
    background: rgba(40, 122, 237, 0.08)
    border: 1px solid rgba(40, 122, 237, 0.28)
    border-radius: 8px
    padding: 9px 14px
    transition: background .18s ease
    &:hover
      background: rgba(40, 122, 237, 0.16)

  // ---------- LISTAS ----------
  &__list
    display: flex
    flex-direction: column
    gap: 10px

  &__row
    display: flex
    align-items: center
    gap: 14px
    background: $paper
    border: 1px solid $line
    border-radius: 14px
    padding: 14px 16px

  &__row-avatar
    display: grid
    place-items: center
    flex-shrink: 0
    width: 44px
    height: 44px
    border-radius: 50%
    font-family: $display
    font-weight: 700
    font-size: 16px
    color: #fff
    background: linear-gradient(150deg, $blue, $blue-dark 55%, $red)
    &--bot
      font-size: 18px
      background: linear-gradient(150deg, #33405a, $ink)
      overflow: hidden

  &__row-photo
    width: 100%
    height: 100%
    object-fit: cover

  &__row-body
    flex: 1 1 auto
    min-width: 0
    display: flex
    flex-direction: column
    gap: 2px

  &__row-name
    font-family: $display
    text-transform: uppercase
    font-size: 18px
    font-weight: 700
    line-height: 1.05
    color: $ink
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  &__row-sub
    font-size: 13px
    color: $muted
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  &__row-desc
    font-size: 12px
    color: $muted
    margin-top: 2px
    display: -webkit-box
    -webkit-line-clamp: 2
    -webkit-box-orient: vertical
    overflow: hidden

  &__row--off
    opacity: 0.62

  &__row-badge
    display: inline-flex
    align-items: center
    gap: 5px
    flex-shrink: 0
    font-family: $mono
    font-size: 10px
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 0.04em
    color: #B8860B
    background: rgba(255, 200, 87, 0.14)
    border: 1px solid rgba(255, 200, 87, 0.4)
    border-radius: 999px
    padding: 4px 9px
    &-ic
      font-size: 10px
    &--on
      color: #2E7D32
      background: rgba(46, 125, 50, 0.10)
      border-color: rgba(46, 125, 50, 0.3)
    &--off
      color: $muted
      background: rgba(92, 102, 115, 0.10)
      border-color: rgba(92, 102, 115, 0.3)

  &__row-del
    display: grid
    place-items: center
    flex-shrink: 0
    width: 34px
    height: 34px
    border-radius: 8px
    cursor: pointer
    color: $red
    background: rgba(221, 52, 52, 0.08)
    border: 1px solid rgba(221, 52, 52, 0.22)
    transition: background .18s ease
    &:hover
      background: rgba(221, 52, 52, 0.18)

  &__row-edit
    color: $blue
    background: rgba(40, 122, 237, 0.08)
    border-color: rgba(40, 122, 237, 0.22)
    &:hover
      background: rgba(40, 122, 237, 0.18)

  &__empty
    padding: 20px 22px
    background: $paper
    border: 1px dashed $line
    border-radius: 14px
    color: $muted
    font-size: 14px

  // ---------- AÇÕES (cards) ----------
  &__actions
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr))
    gap: 14px

  // ---------- HINT ----------
  &__hint
    display: flex
    align-items: center
    gap: 18px
    padding: 22px 24px
    background: $paper
    border: 1px solid $line
    border-radius: 16px
    flex-wrap: wrap
    &-ic
      display: grid
      place-items: center
      flex-shrink: 0
      width: 52px
      height: 52px
      border-radius: 12px
      font-size: 22px
      color: $blue
      background: rgba(40, 122, 237, 0.10)
    &-txt
      flex: 1 1 220px
      display: flex
      flex-direction: column
      gap: 4px
      strong
        font-family: $display
        text-transform: uppercase
        font-size: 19px
        font-weight: 700
        color: $ink
      span
        font-size: 14px
        color: $muted
    &-btn
      display: inline-flex
      align-items: center
      gap: 10px
      cursor: pointer
      font-family: $display
      font-weight: 600
      text-transform: uppercase
      letter-spacing: 0.06em
      font-size: 14px
      color: $red
      background: rgba(221, 52, 52, 0.08)
      border: 1px solid rgba(221, 52, 52, 0.28)
      border-radius: 6px
      padding: 11px 18px
      transition: background .18s ease
      &:hover
        background: rgba(221, 52, 52, 0.16)
      &-ic
        font-size: 12px

.actcard
  display: flex
  align-items: center
  gap: 16px
  text-align: left
  cursor: pointer
  font-family: inherit
  background: $paper
  border: 1px solid $line
  border-radius: 14px
  padding: 18px 20px
  transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease
  &:hover
    transform: translateY(-4px)
    box-shadow: 0 18px 38px rgba(11, 14, 20, 0.12)
    border-color: rgba(40, 122, 237, 0.4)
    .actcard__arrow
      transform: translateX(4px)
      color: $blue
  &__ic
    display: grid
    place-items: center
    width: 46px
    height: 46px
    border-radius: 12px
    flex-shrink: 0
    font-size: 19px
    color: $blue
    background: rgba(40, 122, 237, 0.10)
  &:nth-child(even)
    .actcard__ic
      color: $red
      background: rgba(221, 52, 52, 0.10)
  &__txt
    flex: 1 1 auto
    min-width: 0
    display: flex
    flex-direction: column
    gap: 4px
    strong
      font-family: $display
      text-transform: uppercase
      font-size: 19px
      font-weight: 700
      line-height: 1
      color: $ink
    span
      font-size: 13px
      color: $muted
  &__arrow
    flex-shrink: 0
    font-size: 13px
    color: #C4C9D2
    transition: transform .22s ease, color .22s ease

// ---------- MODAL ----------
::v-deep .modal
  border-radius: 14px

.tmodal
  padding: 36px 32px 30px
  max-height: 80vh
  overflow-y: auto

  &__step
    display: flex
    flex-direction: column

  &__title
    font-family: $display
    font-weight: 700
    text-transform: uppercase
    font-size: 22px
    margin: 0 0 18px
    color: $ink

  &__label
    font-size: 13px
    font-weight: 600
    color: $muted
    margin: 12px 0 6px

  &__input
    border: none
    border-bottom: 1.5px solid $line
    padding: 8px 2px
    font-size: 15px
    font-family: inherit
    color: $ink
    &:focus
      outline: none
      border-color: $blue

  &__select
    margin: 4px 0

  &__textarea
    border: 1.5px solid $line
    border-radius: 8px
    padding: 10px 12px
    font-size: 15px
    font-family: inherit
    color: $ink
    resize: vertical
    &:focus
      outline: none
      border-color: $blue

  &__photo
    display: flex
    align-items: center
    gap: 16px
    margin: 4px 0 8px

  &__photo-preview
    display: grid
    place-items: center
    flex-shrink: 0
    width: 72px
    height: 72px
    border-radius: 14px
    overflow: hidden
    font-size: 26px
    color: #fff
    background: linear-gradient(150deg, #33405a, $ink)
    img
      width: 100%
      height: 100%
      object-fit: cover

  &__photo-btn
    display: inline-flex
    align-items: center
    gap: 8px
    cursor: pointer
    font-family: $mono
    font-size: 12px
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 0.04em
    color: $blue
    background: rgba(40, 122, 237, 0.08)
    border: 1px solid rgba(40, 122, 237, 0.28)
    border-radius: 8px
    padding: 9px 14px
    transition: background .18s ease
    &:hover
      background: rgba(40, 122, 237, 0.16)

  &__photo-input
    display: none

  &__switch
    display: inline-flex
    align-items: center
    gap: 10px
    cursor: pointer
    margin: 18px 0 4px
    input
      position: absolute
      opacity: 0
      pointer-events: none
    &-track
      position: relative
      width: 42px
      height: 24px
      border-radius: 999px
      background: rgba(11, 14, 20, 0.18)
      transition: background .18s ease
      flex-shrink: 0
      &::after
        content: ''
        position: absolute
        top: 3px
        left: 3px
        width: 18px
        height: 18px
        border-radius: 50%
        background: #fff
        transition: transform .18s ease
    input:checked + .tmodal__switch-track
      background: #2E7D32
      &::after
        transform: translateX(18px)
    &-lbl
      font-size: 14px
      font-weight: 600
      color: $ink

  &__hint
    font-size: 12px
    color: $muted
    margin: 8px 0 0

  &__err
    font-size: 13px
    color: $red
    margin: 12px 0 0

  &__ok
    font-size: 13px
    color: #2E7D32
    margin: 12px 0 0

  &__picks
    display: flex
    flex-wrap: wrap
    gap: 8px
    margin: 6px 0 4px

  &__foot
    margin-top: 26px

.pick
  display: inline-flex
  align-items: center
  gap: 9px
  cursor: pointer
  background: $paper
  border: 1.5px solid $line
  border-radius: 10px
  padding: 8px 12px
  transition: border-color .18s ease, background .18s ease
  &__input
    position: absolute
    opacity: 0
    pointer-events: none
  &__check
    display: grid
    place-items: center
    width: 20px
    height: 20px
    border-radius: 6px
    border: 2px solid rgba(11, 14, 20, 0.18)
    color: transparent
    font-size: 10px
    transition: all .18s ease
  &__name
    font-size: 14px
    font-weight: 600
    color: $ink
  &--on
    border-color: $blue
    background: rgba(40, 122, 237, 0.05)
    .pick__check
      background: $blue
      border-color: $blue
      color: #fff

@keyframes team-rise
  from
    opacity: 0
    transform: translateY(16px)
  to
    opacity: 1
    transform: none

@media (max-width: 620px)
  .team__hero
    padding: 30px 0 40px
  .team__hero-inner
    padding: 0 18px
  .team__wrap
    padding: 32px 18px 52px
</style>
